import React, { Component } from 'react';
import { Checkbox, Grid } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { cloneDeep, filter, find, isEmpty, isNil, sortBy } from 'lodash';
import { connect } from 'react-redux';
import Color from 'color';
import * as classnames from 'classnames';
import { updateLayer } from '../../../data/analysis/layersDispatchers';
import ColorPicker, { SIGNAL_COLORS } from '../ColorPicker';
import ReorderTool from '../ReorderTool';
import {
  abstractTypesSortingHandler,
  setOpacityForLayerSignalColor,
  setProjectAnalysisActiveAbstractType
} from '../../../data/analysis/signalsActions';

const defaultColors = {
  red: 255,
  green: 255,
  blue: 255,
  alpha: 50
};

const SignalColor = props => {
  const { red, green, blue, alpha, clickHandler } = props;
  const style = {
    backgroundColor: `rgba(${red},${green},${blue},${alpha})`
  };
  return <div style={style} className="signalColor" onClick={clickHandler} />;
};

SignalColor.propTypes = {
  red: PropTypes.number,
  green: PropTypes.number,
  blue: PropTypes.number,
  alpha: PropTypes.number
};

SignalColor.defaultProps = defaultColors;

class SignalsBox extends Component {
  constructor(props) {
    super(props);
    this.setActiveSignal = this.setActiveSignal.bind(this);
    this.setActiveDimension = this.setActiveDimension.bind(this);
    this.closeColorPicker = this.closeColorPicker.bind(this);
    this.openColorPicker = this.openColorPicker.bind(this);
    this.okHandler = this.okHandler.bind(this);

    this.upHandler = this.upHandler.bind(this);
    this.downHandler = this.downHandler.bind(this);
    this.clearHandler = this.clearHandler.bind(this);

    this.state = {
      colorPickerIsOpen: false,
      colorPickerSelectedSettings: null
    };
  }

  setVisibleSection() {
    this.setState(prevState => ({
      hidden: !prevState.hidden
    }));
  }

  setActiveSignal(id) {
    const { dispatch } = this.props;
    dispatch(setProjectAnalysisActiveAbstractType(id, 'signal'));
  }

  setActiveDimension(id) {
    const { dispatch } = this.props;
    dispatch(setProjectAnalysisActiveAbstractType(id, 'dimension'));
  }

  getSignalAssociationColor(abstractTypeId) {
    const { layer, signalsColors } = this.props;
    if (!layer || !signalsColors[layer.id] || !signalsColors[layer.id][abstractTypeId]) {
      return undefined;
    }
    const association = layer.layer_abstract_type_associations.find(item => {
      return item.abstract_type_id === abstractTypeId;
    });

    return isNil(association) ? undefined : signalsColors[layer.id][abstractTypeId];
  }

  updateLayer(abstractTypeId, color = null, alphaValue) {
    const { dispatch, activeLayerId, signalsColors, layer } = this.props;
    const layerClone = cloneDeep(layer);

    const index = layerClone.layer_abstract_type_associations.findIndex(item => {
      return item.abstract_type_id === abstractTypeId;
    });

    if (!isNil(alphaValue)) {
      dispatch(setOpacityForLayerSignalColor(layerClone.id, abstractTypeId, alphaValue));
    }

    if (color) {
      const association = {
        color_r: color.red(),
        color_g: color.green(),
        color_b: color.blue()
      };
      association.abstract_type_id = abstractTypeId;
      association.layer_id = activeLayerId;

      if (index >= 0) {
        layerClone.layer_abstract_type_associations[index].color_r = association.color_r;
        layerClone.layer_abstract_type_associations[index].color_g = association.color_g;
        layerClone.layer_abstract_type_associations[index].color_b = association.color_b;
      } else {
        layerClone.layer_abstract_type_associations.push(association);
      }
    } else if (index >= 0) {
      layerClone.layer_abstract_type_associations.splice(index, 1);
    } else {
      const randomColorX = Math.floor(Math.random() * Math.floor(SIGNAL_COLORS.length));
      const randomColorY = Math.floor(
        Math.random() * Math.floor(SIGNAL_COLORS[randomColorX].length)
      );
      const rgbaColor = Color(SIGNAL_COLORS[randomColorX][randomColorY]);

      let r = rgbaColor.red();
      let g = rgbaColor.green();
      let b = rgbaColor.blue();

      // See if color already set once before un store
      const existingLayerColors = find(signalsColors, (signalColor, layerId) => {
        return parseInt(layerId, 10) === layerClone.id;
      });

      if (existingLayerColors && abstractTypeId in existingLayerColors) {
        [r, g, b] = existingLayerColors[abstractTypeId].color;
      }
      const association = {
        color_r: r,
        color_g: g,
        color_b: b
      };

      association.abstract_type_id = abstractTypeId;
      association.layer_id = activeLayerId;
      layerClone.layer_abstract_type_associations.push(association);
    }

    // update layerClone
    dispatch(updateLayer(layerClone));
  }

  upHandler() {
    const { dispatch } = this.props;
    dispatch(abstractTypesSortingHandler('up'));
  }

  downHandler() {
    const { dispatch } = this.props;
    dispatch(abstractTypesSortingHandler('down'));
  }

  clearHandler() {
    const { dispatch } = this.props;
    dispatch(setProjectAnalysisActiveAbstractType(null));
  }

  closeColorPicker() {
    this.setState({
      colorPickerIsOpen: false,
      colorPickerSelectedSettings: null
    });
  }

  openColorPicker(colors) {
    this.setState({
      colorPickerIsOpen: true,
      colorPickerSelectedSettings: colors
    });
  }

  okHandler(color, alphaValue) {
    const { activeAbstractTypeId } = this.props;
    const rgbaColor = Color(color);
    this.closeColorPicker();
    this.updateLayer(activeAbstractTypeId, rgbaColor, alphaValue);
  }

  render() {
    const { signals, activeAbstractTypeId } = this.props;
    const { colorPickerSelectedSettings, colorPickerIsOpen } = this.state;
    let evenMarker = 0;
    const self = this;

    const mappedSignals = sortBy(
      filter(signals, signal => {
        return (
          signal.is_analysable || filter(signal.dimensions, { is_analysable: true }).length > 0
        );
      }),
      signal => {
        return self.props.signalsSorting.indexOf(signal.id);
      }
    ).map((signal, index) => {
      const signalClassName = classnames({
        signal: true,
        selected: activeAbstractTypeId === signal.id,
        odd: evenMarker % 2 === 0,
        clickable: true
      });

      const analysableDimensions = sortBy(
        filter(signal.dimensions, { is_analysable: true }),
        dimension => {
          return self.props.signalDimensionsSorting[signal.id]
            ? self.props.signalDimensionsSorting[signal.id].indexOf(dimension.abstract_type_id)
            : 0;
        }
      );

      const colors = this.getSignalAssociationColor(signal.id);
      const checked = typeof colors !== 'undefined';
      let dimensions = [];

      if (!isEmpty(analysableDimensions)) {
        dimensions = analysableDimensions.map((dimension, dIndex) => {
          evenMarker += 1;

          const dimensionClassName = classnames({
            signal: true,
            selected: activeAbstractTypeId === dimension.abstract_type_id,
            odd: evenMarker % 2 === 0,
            clickable: true
          });

          const dColors = this.getSignalAssociationColor(dimension.abstract_type_id);
          const dChecked = typeof dColors !== 'undefined';

          return (
            <Grid.Row
              className={dimensionClassName}
              key={dimension.id}
              onClick={() => {
                this.setActiveDimension(dimension.abstract_type_id);
              }}
            >
              <Grid.Column width={1} style={{ padding: '0px' }} />
              <Grid.Column width={3} className="color">
                <SignalColor
                  clickHandler={() => {
                    this.openColorPicker(dColors);
                  }}
                  red={dColors && dColors.color ? dColors.color[0] : undefined}
                  green={dColors && dColors.color ? dColors.color[1] : undefined}
                  blue={dColors && dColors.color ? dColors.color[2] : undefined}
                  alpha={dColors && dColors.opacity ? dColors.opacity : undefined}
                />
              </Grid.Column>
              <Grid.Column width={9}>{dimension.name}</Grid.Column>
              <Grid.Column width={3} className="checkboxContainer">
                <Checkbox
                  checked={dChecked}
                  onClick={() => {
                    this.updateLayer(dimension.abstract_type_id);
                  }}
                />
              </Grid.Column>
            </Grid.Row>
          );
        });
      }

      evenMarker += 1;

      if (!signal.is_analysable) {
        return [
          <Grid.Row
            className={signalClassName}
            key={signal.id}
            onClick={() => {
              this.setActiveSignal(signal.id);
            }}
          >
            <Grid.Column width={3} style={{ textAlign: 'center' }}>
              -
            </Grid.Column>
            <Grid.Column width={13}>{signal.name}</Grid.Column>
          </Grid.Row>,
          ...dimensions
        ];
      }
      return [
        <Grid.Row
          className={signalClassName}
          key={signal.id}
          onClick={() => {
            this.setActiveSignal(signal.id);
          }}
        >
          <Grid.Column width={3} className="color">
            <SignalColor
              clickHandler={() => {
                this.openColorPicker(colors);
              }}
              red={colors && colors.color ? colors.color[0] : undefined}
              green={colors && colors.color ? colors.color[1] : undefined}
              blue={colors && colors.color ? colors.color[2] : undefined}
              alpha={colors && colors.opacity ? colors.opacity : undefined}
            />
          </Grid.Column>
          <Grid.Column width={10}>{signal.name}</Grid.Column>
          <Grid.Column width={3} className="checkboxContainer">
            <Checkbox
              checked={checked}
              onClick={() => {
                this.updateLayer(signal.id);
              }}
            />
          </Grid.Column>
        </Grid.Row>,
        ...dimensions
      ];
    });

    return (
      <div>
        <ReorderTool
          upHandler={this.upHandler}
          downHandler={this.downHandler}
          clearHandler={this.clearHandler}
        />

        <Grid className="signalsList">{mappedSignals}</Grid>
        <ColorPicker
          className="colorPicker"
          isOpen={colorPickerIsOpen}
          selectedSettings={colorPickerSelectedSettings}
          onRequestClose={this.closeColorPicker}
          okHandler={this.okHandler}
        />
      </div>
    );
  }
}

export default connect(state => ({
  layer: (() => {
    const { activeLayerId } = state.analysis.single;
    return find(state.analysis.single.layers, { id: activeLayerId });
  })(),
  signals: state.analysis.single.signals,
  activeAbstractTypeId: state.analysis.single.activeAbstractTypeId,
  activeAbstractType: state.analysis.single.activeAbstractType,
  signalsSorting: (() => {
    const { activeLayerId } = state.analysis.single;
    if (isNil(activeLayerId) || !(activeLayerId in state.analysis.single.signalsSorting)) {
      return [];
    }
    return state.analysis.single.signalsSorting[activeLayerId];
  })(),
  signalDimensionsSorting: (() => {
    const { activeLayerId } = state.analysis.single;
    if (isNil(activeLayerId) || !(activeLayerId in state.analysis.single.signalDimensionsSorting)) {
      return [];
    }
    return state.analysis.single.signalDimensionsSorting[activeLayerId];
  })(),
  signalsColors: state.analysis.single.signalsColors,
  activeLayerId: state.analysis.single.activeLayerId
}))(SignalsBox);



// WEBPACK FOOTER //
// ./src/scenes/analysis/components/analysis/parameters/SignalsBox.js